import React, { useState, useRef, useEffect } from 'react';
import { useFont } from '../../Design/FontContext';
import { getTextEffectStyles } from '../utils/textEffectsUtils';
import { getResponsiveFontSize } from '../utils/responsiveFontUtils';
import DeleteModal from './DeleteModal';
import './WidgetStyle.css';
import './ResponsiveFonts.css';
import '../WidgetEditComponents/TextEffectsAnimations.css';
import { generateLayoutStyles, getResponsiveValue, appendPx } from '../WidgetEditComponents/LayoutUtils';
import AIRefreshIcon from '../../components/AIRefreshIcon';

// appendPx wird jetzt aus LayoutUtils importiert

const InfiniteBanner = ({ data, isSelected, onRemove, isEditMode, onUpdate, onSelectWidget }) => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isMobileLayout, setIsMobileLayout] = useState(false);
    const widgetRef = useRef(null);
    const containerRef = useRef(null);
    const { fonts } = useFont();
    
    const deleteLogo = '/img/delete.svg';

    // Mobile Layout Detection (750px Breakpoint)
    useEffect(() => {
        const checkContainerWidth = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.offsetWidth;
                setIsMobileLayout(containerWidth < 750);
            }
        };

        checkContainerWidth();
        window.addEventListener('resize', checkContainerWidth);
        
        const observer = new ResizeObserver(checkContainerWidth);
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            window.removeEventListener('resize', checkContainerWidth);
            observer.disconnect();
        };
    }, []);

    const handleDelete = (event) => {
        onRemove(event, data.id);
        setShowDeleteModal(false);
    };

    // 🚀 RESPONSIVE LAYOUT STYLES - Mit Revolutionary Layout System
    const layoutStyles = generateLayoutStyles(data, isMobileLayout, {
        backgroundColor: data.backgroundColor,
        borderRadius: '0',
        padding: '0',
        border: 'none',
        position: 'relative',
        overflow: 'hidden',
        cursor: isEditMode ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        width: '100vw',
        marginLeft: 'calc(-50vw + 50%)'
    });

    // Widget Styles - Clean Rand-zu-Rand Design mit Revolutionary Layout
    const widgetStyle = {
        ...layoutStyles,
        // InfiniteBanner-spezifische Height-Logic (basierend auf bannerHeight statt Standard-Height)
        height: `${data.bannerHeight || 60}px`,
        minHeight: `${data.bannerHeight || 60}px`,
        maxHeight: `${data.bannerHeight || 60}px`,
        marginRight: 'calc(-50vw + 50%)',
        boxSizing: 'border-box'
    };

    // Banner Container Style - Komplett ohne Border Radius
    const bannerContainerStyle = {
        position: 'relative',
        width: '100%',
        maxWidth: '100%',
        height: `${data.bannerHeight || 60}px`,
        overflow: 'hidden',
        background: data.bannerBackground || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '0px !important', // ← KEIN Border Radius für scharfe Kanten
        display: 'flex',
        alignItems: 'center', // ← Vertikal zentriert
        justifyContent: 'center', // ← Horizontal zentriert
        boxSizing: 'border-box'
    };

    // Sliding Content Style - Optimiert für nahtlose Animation
    const slidingContentStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: `${data.itemSpacing || 40}px`,
        animation: `slideLeft ${data.animationSpeed || 8}s linear infinite`, // ← Optimiert: 8s für bessere UX
        whiteSpace: 'nowrap',
        width: 'max-content',
        willChange: 'transform'
    };

    // Feature Point Style - Clean ohne Backgrounds/Borders
    const getFeaturePointStyle = (index) => ({
        display: 'flex',
        alignItems: 'center', // ← Vertikal zentriert
        justifyContent: 'center', // ← Horizontal zentriert
        gap: '12px',
        fontSize: `${data.fontSize || 16}px`,
        fontWeight: data.fontWeight || '600',
        color: data.textColor || 'white',
        fontFamily: data.fontFamily || fonts.globalSmallFont,
        letterSpacing: data.letterSpacing || '0px', // ← Letter Spacing Support
        ...getTextEffectStyles(data.textEffect),
        textShadow: data.textShadow ? '0 2px 4px rgba(0, 0, 0, 0.3)' : 'none',
        padding: '12px 20px',
        background: 'transparent',
        borderRadius: '0',
        border: 'none',
        backdropFilter: 'none',
        textAlign: 'center',
        whiteSpace: 'nowrap',
        minHeight: '100%'
    });

    // Create duplicated content for seamless loop
    const featurePoints = data.featurePoints || [
        { text: 'Premium Quality', icon: 'fas fa-star' },
        { text: 'Fast Delivery', icon: 'fas fa-rocket' },
        { text: '24/7 Support', icon: 'fas fa-headset' },
        { text: 'Money Back Guarantee', icon: 'fas fa-shield-alt' }
    ];

    // Duplicate content für nahtlose Animation - Mehr Duplikate für bessere Performance
    const duplicatedContent = [
        ...featurePoints, 
        ...featurePoints, 
        ...featurePoints, 
        ...featurePoints, 
        ...featurePoints
    ]; // ← 5x Duplikation für nahtlose Loop

    // Nur rendern wenn showFeaturePoints aktiv ist
    if (data.showFeaturePoints === false) {
        return null;
    }

    return (
        <>
            {/* Delete Modal - AUSSERHALB des Widget-Containers für korrektes Rendering */}
            {isEditMode && showDeleteModal && (
                <DeleteModal
                    isOpen={showDeleteModal}
                    onClose={() => setShowDeleteModal(false)}
                    onConfirm={handleDelete}
                />
            )}

            <div 
                className="widget-container infinite-banner-widget"
                style={widgetStyle}
                ref={containerRef}
                onClick={() => isEditMode && onSelectWidget && onSelectWidget(data.id)}
            >
                {/* Delete Button */}
                {isEditMode && (
                    <button 
                        className="delete_button" 
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowDeleteModal(true);
                        }}
                    >
                        <img 
                            className="delete_logo" 
                            src={deleteLogo} 
                            alt="Delete" 
                        />
                    </button>
                )}
                
                {/* 🤖 AI REFRESH ICON */}
                {isEditMode && (
                    <AIRefreshIcon 
                        widget={{
                            ...data,
                            type: 'InfiniteBanner'
                        }}
                        spaceId={null}
                        onContentRefreshed={(refreshedWidget) => {
                            console.log('🤖 [INFINITEBANNER] AI-refreshed content received:', refreshedWidget);
                            onUpdate(refreshedWidget);
                        }}
                        position="top-left"
                    />
                )}

            {/* Infinite Banner Container - Properly contained */}
            <div style={bannerContainerStyle} className="banner-scroll-container">
                <div style={slidingContentStyle} className="banner-scroll-content">
                    {duplicatedContent.map((feature, index) => (
                        <React.Fragment key={index}>
                            <div style={getFeaturePointStyle(index)} className="banner-feature-item">
                                <span>{feature.text}</span>
                            </div>
                            {/* Symbol-Trenner zwischen Features */}
                            {data.separatorSymbol && data.separatorSymbol !== 'none' && (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: `${(data.fontSize || 16) * 0.8}px`, // ← Etwas kleiner als Text
                                    color: data.textColor || 'white',
                                    opacity: 0.7, // ← Subtiler als Haupttext
                                    padding: '0 10px'
                                }}>
                                    {data.separatorSymbol === 'dot' && '•'}
                                    {data.separatorSymbol === 'circle' && '○'}
                                    {data.separatorSymbol === 'square' && '◼'}
                                    {data.separatorSymbol === 'diamond' && '◆'}
                                    {data.separatorSymbol === 'star' && '★'}
                                    {data.separatorSymbol === 'heart' && '♥'}
                                    {data.separatorSymbol === 'arrow' && '→'}
                                </div>
                            )}
                        </React.Fragment>
                    ))}
                </div>
            </div>

            {/* CSS Animation - Properly contained */}
            <style>{`
                .infinite-banner-widget {
                    contain: layout style paint size; /* Full CSS containment */
                    max-width: 100% !important;
                    overflow: hidden !important;
                }
                
                .infinite-banner-widget > * {
                    max-width: 100% !important;
                    box-sizing: border-box !important;
                }
                
                @keyframes slideLeft {
                    0% {
                        transform: translateX(0%);
                    }
                    100% {
                        transform: translateX(-20%); /* ← Nur 20% für nahtlose Loop */
                    }
                }
                
                .infinite-banner-widget:hover .delete_button {
                    opacity: 1;
                }
                
                .infinite-banner-widget:hover div[style*="slideLeft"] {
                    animation-play-state: paused;
                }
                
                /* Critical: Force absolute containment */
                .banner-scroll-container {
                    max-width: 100% !important;
                    width: 100% !important;
                    overflow: hidden !important;
                    contain: layout style paint size !important;
                    position: relative !important;
                }
                
                .banner-scroll-content {
                    max-width: none !important; /* Allow content to be wider for animation */
                    position: relative !important;
                }
                
                .banner-feature-item {
                    flex-shrink: 0 !important;
                    white-space: nowrap !important;
                }
                
                /* Ensure parent containers don't overflow */
                .infinite-banner-widget {
                    max-width: 100% !important;
                    overflow: hidden !important;
                    contain: layout style paint size !important;
                }
                
                /* Force all parent elements to respect bounds */
                .widget-boss-wrapper,
                .inner_preview_div,
                .page_preview,
                .outer-preview-div {
                    max-width: 100% !important;
                    overflow-x: hidden !important;
                }
            `}</style>
        </div>
        </>
    );
};

export default InfiniteBanner;
